import { useCallback, useEffect, useMemo, useState } from 'react';
import { ActivityFeed, ActivityItem } from 'app/data/types/activity';

/**
 * Backoff between re-reads after a write, one entry per attempt.
 *
 * The write returns before the read side has it, and how far before is not knowable from here: an event
 * booking reaches the projection over one Kafka hop, while a visit's change is captured off the database,
 * queued as a reindex job, and re-fetched over HTTP before it lands. A single delay tuned for one is
 * either felt or too short for the other, so the feed re-reads on this schedule and stops as soon as the
 * read side agrees — the total is a ceiling, not a wait.
 */
export const SETTLE_BACKOFF_MS = [400, 800, 1600, 2400];

/** A write waiting to show up in the feed. */
export interface PendingWrite {
  /** The tile the write touched. */
  id: string;
  /** Whether the tile still reads as it did before the write. */
  isStale: (item: ActivityItem) => boolean;
  refetch: () => void;
}

/** Every write still being waited on, and how far through the backoff the poll is. */
interface PendingSettle {
  writes: PendingWrite[];
  attempt: number;
}

/**
 * Re-reads the feed after a write until the tile it touched stops reading the way it did before.
 *
 * Staleness is judged across BOTH panes, not the visible one: segment and filter are client-side slices,
 * so a pane switch mid-poll would otherwise hide the tile, end the poll, and leave nothing to correct it
 * on the way back — the switch itself does not refetch.
 */
export const useSettleAfterWrite = (feed: ActivityFeed): ((write: PendingWrite) => void) => {
  const [pending, setPending] = useState<PendingSettle | null>(null);
  const allItems = useMemo(() => [...feed.upcoming, ...feed.completed], [feed]);

  // Cancels come in bursts, and a visit and an event do not read from the same caches — so writes
  // accumulate rather than replace, and a second one cannot abandon the first one's poll. A new write
  // restarts the schedule, since everything outstanding is being waited on again anyway.
  const settleAfterWrite = useCallback((write: PendingWrite) => {
    setPending((current) => ({
      writes: [...(current?.writes ?? []).filter((queued) => queued.id !== write.id), write],
      attempt: 0,
    }));
  }, []);

  // The timer is scoped to the effect, so unmounting mid-poll cancels the wait rather than firing an
  // invalidation for a screen the user has left.
  useEffect(() => {
    if (!pending) return undefined;

    // Recomputed each pass, so a write drops out of the poll as soon as its tile updates or disappears.
    const stale = pending.writes.filter((write) => {
      const item = allItems.find((candidate) => candidate.id === write.id);

      return !!item && write.isStale(item);
    });

    if (!stale.length || pending.attempt >= SETTLE_BACKOFF_MS.length) {
      setPending(null);

      return undefined;
    }

    const timer = setTimeout(() => {
      // Deduped: several stale writes usually read from the same cache.
      new Set(stale.map((write) => write.refetch)).forEach((refetch) => refetch());
      setPending((current) => (current ? { ...current, attempt: current.attempt + 1 } : null));
    }, SETTLE_BACKOFF_MS[pending.attempt]);

    return () => clearTimeout(timer);
  }, [allItems, pending]);

  return settleAfterWrite;
};
